import type { QueryClient } from '@tanstack/react-query';
import type { ConversationSearchRefreshState } from '../types/conversation-search-refresh.types';

const DOCUMENT_QUERY_KEY = ['crm/documents/funnel-conversations'];
const BATCH_DELAY_MS = 600;
const schedulers = new WeakMap<QueryClient, ConversationSearchRefreshState>();

const hasActiveQueries = (queryClient: QueryClient): boolean =>
  queryClient.getQueryCache().findAll({
    queryKey: DOCUMENT_QUERY_KEY,
    type: 'active',
  }).length > 0;

/**
 * Coalesces realtime board recovery without cancelling an existing HTTP read.
 * Notifications during a read share one trailing refresh so its older response
 * cannot consume the only recovery opportunity. Calls before a batch starts
 * share that batch; each caller completes after its applicable refresh.
 *
 * @param queryClient - Owner of the board queries and transient scheduler.
 * @returns Completion of the applicable refresh, or cleanup of its owner.
 */
export const refreshConversationSearch = (
  queryClient: QueryClient
): Promise<void> => {
  // Preserve immediate staleness for inactive views without starting HTTP work.
  void queryClient.invalidateQueries({
    queryKey: DOCUMENT_QUERY_KEY,
    refetchType: 'none',
  });
  if (!hasActiveQueries(queryClient)) return Promise.resolve();

  let state = schedulers.get(queryClient);
  if (!state) {
    state = { stopped: false, unsubscribe: () => undefined };
    schedulers.set(queryClient, state);
  }
  const owner = state;
  // Re-read mutable lifecycle state after awaits; notifications/cleanup can
  // change it while this batch is suspended.
  const isStopped = (): boolean => owner.stopped;
  const hasPending = (): boolean => owner.pending !== undefined;
  const dispose = (): void => {
    owner.stopped = true;
    window.clearTimeout(owner.timer);
    owner.finishDelay?.();
    owner.unsubscribe();
    if (schedulers.get(queryClient) === owner) schedulers.delete(queryClient);
  };
  if (owner.pending) return owner.pending;

  if (!owner.running) {
    owner.unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (
        event.type === 'removed' &&
        queryClient.getQueryCache().findAll({ queryKey: DOCUMENT_QUERY_KEY })
          .length === 0
      ) {
        dispose();
      }
    });
  }

  const pending = (owner.running ?? Promise.resolve()).then(async () => {
    if (isStopped()) return;
    await new Promise<void>((resolve) => {
      owner.finishDelay = resolve;
      owner.timer = window.setTimeout(resolve, BATCH_DELAY_MS);
    });
    owner.finishDelay = undefined;
    owner.timer = undefined;
    if (isStopped()) return;
    owner.pending = undefined;
    if (!hasActiveQueries(queryClient)) {
      dispose();
      return;
    }
    const running = Promise.resolve()
      .then(async () => {
        // An ordinary read may predate this notification. Let it finish, then
        // start recovery; merely joining it could leave its old result visible.
        await queryClient.refetchQueries(
          {
            queryKey: DOCUMENT_QUERY_KEY,
            type: 'active',
            fetchStatus: 'fetching',
          },
          { cancelRefetch: false }
        );
        if (isStopped() || !hasActiveQueries(queryClient)) return;
        await queryClient.invalidateQueries(
          { queryKey: DOCUMENT_QUERY_KEY, refetchType: 'active' },
          { cancelRefetch: false }
        );
      })
      // React Query retains request errors; they must not block a trailing batch.
      .catch(() => undefined);
    owner.running = running;
    await running;
    owner.running = undefined;
    if (!hasPending()) dispose();
  });
  owner.pending = pending;
  return pending;
};
